export type FileDeliveryResult = 'shared' | 'downloaded' | 'cancelled' | 'failed';

interface FileShareNavigator {
  canShare?: (data: { files?: File[] }) => boolean;
  share?: (data: { files?: File[]; title?: string }) => Promise<void>;
}

function downloadBlob(blob: Blob, filename: string): boolean {
  try {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Revoking immediately can cancel the download in Safari.
    window.setTimeout(() => URL.revokeObjectURL(url), 10000);
    return true;
  } catch {
    return false;
  }
}

/**
 * Hands a generated file to the user: the system share sheet where files are
 * shareable (iOS Safari and installed PWAs, where `download` is ignored and
 * nothing reaches Photos), a plain download everywhere else.
 *
 * Must be called from a user gesture, and the blob should already exist — the
 * share sheet is blocked once the activation from the tap has expired.
 */
export async function shareOrDownloadFile(blob: Blob, filename: string, title?: string): Promise<FileDeliveryResult> {
  const nav = navigator as Navigator & FileShareNavigator;
  const file = new File([blob], filename, { type: blob.type || 'application/octet-stream' });

  if (typeof nav.canShare === 'function' && typeof nav.share === 'function' && nav.canShare({ files: [file] })) {
    try {
      await nav.share({ files: [file], title });
      return 'shared';
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return 'cancelled';
      // Anything else (share unavailable mid-flight, permission) falls back to a download.
    }
  }

  return downloadBlob(blob, filename) ? 'downloaded' : 'failed';
}
